import { useCallback } from 'react';
import type { GridStackWidget } from 'gridstack';
import { updateParameter } from '../../Components/CodeEditor/Devices/DeviceCardBuilder';
import { getMainProgramCode } from '../../Components/CodeEditor/utils/mainProgramEditor';
import { replaceConstructorArgInProgramCode } from '../../utils/deviceProgramCodeUtils';
import type { DeviceCardProps } from '../../Components/CodeEditor/Devices/DeviceCardProps';
import type { DashboardWidgetContent } from '../../Components/Dashboard/dashboardTypes';
import {
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  sensorClassNameFromStreamKey,
  streamPortFromSensorKey,
} from '../../Components/Graphs/sensor-utils';
import { FrameworkClasses } from '../../framework/frameworkClasses';
import { useAppDispatch, useAppSelector } from '../../redux/ReduxHooks';
import { projectSlice } from '../../redux/ProjectSlice';
import type { AppDispatch } from '../../redux/Store';
import { persistDashboardLayoutToProject } from '../../Components/Dashboard/persistDashboardWidgetContent';
import { DRIVE_UNIT_PORT_PARAMETERS } from '../../utils/driveUnitPortSelection';
import { isDriveUnitPortParameterName } from '../../utils/frameworkArgTypes';
import { remapDriveUnitPortInWidgetContent } from '../../utils/dashboardWidgetPortRemap';
import { parseDashboardWidgetContent, rebuildDashboardProgramCodeFromLayout } from '../../utils/sensor-stream-tracker';

type ConfigurePanelPortChangeProps = Pick<
  DeviceCardProps,
  'id' | 'parameterName' | 'editor' | 'lineNumber' | 'parentName' | 'parentClass' | 'mechanismClass'
> & {
  skipEditorParameterSync?: boolean;
};

/** Parses `electronicId:parameterName` (e.g. `highspeedmotor-m1:motor_port`) to the device variable name. */
function variableNameFromElectronicKey(electronic: string): string {
  const id = electronic.slice(0, electronic.lastIndexOf(':'));
  const dashIdx = id.indexOf('-');
  return dashIdx >= 0 ? id.slice(dashIdx + 1) : id;
}

function frameworkClassNameFromElectronicKey(electronic: string): string | null {
  const id = electronic.slice(0, electronic.lastIndexOf(':'));
  const dashIdx = id.indexOf('-');
  const classPrefix = (dashIdx >= 0 ? id.slice(0, dashIdx) : id).toLowerCase();
  const frameworkClass = FrameworkClasses.find((candidate) => (candidate.uiPanel ?? candidate.name).toLowerCase() === classPrefix);
  return frameworkClass?.name ?? null;
}

function streamKeyUsesPort(sensorKey: string, port: number): boolean {
  const usesDriveUnitPort =
    isMotorStreamSensorKey(sensorKey) || isElectronicSensorStreamSensorKey(sensorKey) || isOtherElectronicsStreamSensorKey(sensorKey);
  return usesDriveUnitPort && streamPortFromSensorKey(sensorKey) === port;
}

function widgetContentUsesStreamPort(content: DashboardWidgetContent, port: number): boolean {
  if (streamKeyUsesPort(content.dataKey, port)) return true;
  return content.sensorKeys.some((key) => streamKeyUsesPort(key, port));
}

function widgetContentMatchesSensorClass(content: DashboardWidgetContent, sensorClassName: string | null): boolean {
  if (!sensorClassName) return false;
  const widgetSensorClassName = content.options?.sensorClassName;
  if (widgetSensorClassName) return widgetSensorClassName === sensorClassName;

  return [content.dataKey, ...content.sensorKeys].some((key) => sensorClassNameFromStreamKey(key) === sensorClassName);
}

function shouldRemapWidgetContentForElectronic(
  content: DashboardWidgetContent,
  variableName: string,
  oldPort: number,
  parameterName: string,
  sensorClassName: string | null
): boolean {
  if (!widgetContentUsesStreamPort(content, oldPort)) return false;
  if (parameterName === DRIVE_UNIT_PORT_PARAMETERS.MOTOR) {
    const widgetVariableName = content.options?.motorVariableName;
    if (widgetVariableName) return widgetVariableName === variableName;
  }
  if (parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR) {
    const widgetVariableName = content.options?.sensorVariableName;
    if (widgetVariableName) return widgetVariableName === variableName;
    return widgetContentMatchesSensorClass(content, sensorClassName);
  }
  return true;
}

function remapDashboardLayoutForElectronicPortChange(
  layout: GridStackWidget[],
  variableName: string,
  oldPort: number,
  newPort: number,
  parameterName: string,
  sensorClassName: string | null
): GridStackWidget[] {
  let changed = false;
  const next = layout.map((widget) => {
    const parsed = parseDashboardWidgetContent(widget.content);
    if (!parsed || !shouldRemapWidgetContentForElectronic(parsed, variableName, oldPort, parameterName, sensorClassName)) return widget;

    changed = true;
    return { ...widget, content: JSON.stringify(remapDriveUnitPortInWidgetContent(parsed, oldPort, newPort)) };
  });

  return changed ? next : layout;
}

/** Drive-unit port changes that also remap dashboard `set_request_list` stream keys. */
export function portChangeSyncsDashboardStreams(parameterName: string): boolean {
  return parameterName === DRIVE_UNIT_PORT_PARAMETERS.MOTOR || parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR;
}

/** Skips the next stream-sync in `useDriveUnitPortRegistry` after a configure-panel port commit (already synced). */
export const skipNextDriveUnitPortStreamSyncRef = { current: false };

/** Remaps dashboard widgets and `set_request_list` when a drive-unit port changes (constructor already in program code). */
export function applyDriveUnitPortStreamSync(
  dispatch: AppDispatch,
  opts: {
    readOnly?: boolean;
    projectId?: string;
    dashboardLayout: GridStackWidget[] | undefined;
    programCode: string;
    originalProgramCode: string;
    electronic: string;
    parameterName: string;
    oldPort: number;
    newPort: number;
  }
): void {
  if (opts.readOnly || !opts.projectId) return;

  const variableName = variableNameFromElectronicKey(opts.electronic);
  const sensorClassName = opts.parameterName === DRIVE_UNIT_PORT_PARAMETERS.SENSOR ? frameworkClassNameFromElectronicKey(opts.electronic) : null;
  const layout = opts.dashboardLayout ?? [];
  const nextLayout = portChangeSyncsDashboardStreams(opts.parameterName)
    ? remapDashboardLayoutForElectronicPortChange(layout, variableName, opts.oldPort, opts.newPort, opts.parameterName, sensorClassName)
    : layout;
  const layoutChanged = nextLayout !== layout;

  if (layoutChanged) {
    persistDashboardLayoutToProject(dispatch, nextLayout);
  }

  const nextCode = rebuildDashboardProgramCodeFromLayout(opts.programCode, nextLayout);
  if (nextCode !== opts.originalProgramCode) {
    dispatch(
      projectSlice.actions.queueProgramReplacement({
        code: nextCode,
        projectId: opts.projectId,
        lastModified: new Date().toISOString(),
      })
    );
  }
}

/**
 * Commits a drive-unit port change from the configure hardware panel.
 * For drive-unit port parameters with dashboard stream sync (e.g. `motor_port`), updates the constructor in program
 * code and dashboard streams, then `setPort`; manual Monaco edits are handled by `useDriveUnitPortRegistry`.
 * Other parameters use Monaco `updateParameter`.
 */
export function useConfigurePanelPortChange(props: ConfigurePanelPortChangeProps, port: number | undefined, setPort: (value: number) => void) {
  const dispatch = useAppDispatch();
  const loadedProject = useAppSelector((state) => state.project.loadedProject);
  const readOnly = loadedProject?.readOnly;
  const projectId = loadedProject?.id;
  const dashboardLayout = loadedProject?.dashboardLayout;
  const programCode = loadedProject?.program?.code;

  return useCallback(
    (portNumber: number) => {
      const oldPort = port;
      const isDriveUnitPortChange =
        isDriveUnitPortParameterName(props.parameterName) && typeof oldPort === 'number' && Number.isFinite(oldPort) && oldPort !== portNumber;
      const usesStreamSyncPath = isDriveUnitPortChange && portChangeSyncsDashboardStreams(props.parameterName);

      if (usesStreamSyncPath) {
        if (!readOnly && projectId && !props.skipEditorParameterSync) {
          const originalProgramCode = getMainProgramCode(props.editor, programCode ?? '');
          const codeWithConstructor = replaceConstructorArgInProgramCode(
            originalProgramCode,
            props.lineNumber,
            props.parentName,
            props.parentClass,
            props.mechanismClass,
            props.parameterName,
            portNumber.toString()
          );
          skipNextDriveUnitPortStreamSyncRef.current = true;
          applyDriveUnitPortStreamSync(dispatch, {
            readOnly,
            projectId,
            dashboardLayout,
            programCode: codeWithConstructor,
            originalProgramCode,
            electronic: `${props.id}:${props.parameterName}`,
            parameterName: props.parameterName,
            oldPort,
            newPort: portNumber,
          });
        }
        setPort(portNumber);
        return;
      }

      setPort(portNumber);

      if (!props.skipEditorParameterSync) {
        updateParameter(props as DeviceCardProps, portNumber.toString());
      }
    },
    [dispatch, readOnly, projectId, dashboardLayout, programCode, port, props, setPort]
  );
}
